/*
 * Rebuilds the selected filter elements from the list URL parameters, so the active
 * filters bar can display the chips after a page refresh or a direct link.
 *
 * `sources` only needs to contain the element lists that are loaded AND not hydrated yet:
 * {brands, genders, categories, options, channels, statuses, validated, eshopIds, inStock,
 * limitedDistribution, benchmarks, demands, locks, stocks, withPrice, valueFilters}
 */

// The Price tab's four free numeric inputs: one URL param and one active-filter
// chip each (fixed chip id, so re-validating a field replaces its chip).
export const PRICE_FILTER_FIELDS = [
    {type: "PriceMin", id: "priceMin", param: "priceMin", stateKey: "priceMinFilter", unit: "€", labelId: "retailer.product.list.filter.price.min"},
    {type: "PriceMax", id: "priceMax", param: "priceMax", stateKey: "priceMaxFilter", unit: "€", labelId: "retailer.product.list.filter.price.max"},
    {type: "DiscountMin", id: "discountMin", param: "discountMin", stateKey: "discountMinFilter", unit: "%", labelId: "retailer.product.list.filter.discount.min"},
    {type: "DiscountMax", id: "discountMax", param: "discountMax", stateKey: "discountMaxFilter", unit: "%", labelId: "retailer.product.list.filter.discount.max"},
];

// The Sales tab's single free numeric input: minimum online sales count.
export const SALES_FILTER_FIELDS = [
    {type: "SalesMin", id: "salesMin", param: "salesMin", stateKey: "salesMinFilter", unit: "", labelId: "retailer.product.list.filter.sales.min"},
];

// Every free numeric input across the tabs — the shared wiring (hydration,
// URL dedup, chip removal) iterates this one.
export const VALUE_FILTER_FIELDS = [...PRICE_FILTER_FIELDS, ...SALES_FILTER_FIELDS];

// The chip carries the typed value ("10 €", "50")
export const formatValueFilterName = (value, unit) =>
    unit ? `${Number(value).toLocaleString()} ${unit}` : Number(value).toLocaleString();

const parseJsonIds = (value) => {
    try {
        const ids = JSON.parse(value);

        return Array.isArray(ids) ? ids : [];
    } catch (error) {
        return [];
    }
};

const parseBracesIds = (value) => value.replace(/[{}]/g, "").split(",").filter((id) => id !== "");

const findByIds = (source, ids) => ids
    .map((id) => source.find((element) => String(element.id) === String(id)))
    .filter(Boolean);

const findByValue = (source, value) => source.filter((element) => String(element.value) === String(value));

export function hydrateSelectedFiltersFromParams(paramsString, sources) {
    const params = (paramsString ?? "").split("&");
    const paramValue = (name) => {
        const param = params.find((currentParam) => currentParam.startsWith(`${name}=`));

        return param ? param.substring(name.length + 1) : null;
    };

    const hydrated = [];

    [
        {name: "brand.id", source: sources.brands, parse: parseJsonIds},
        {name: "gender.id", source: sources.genders, parse: parseJsonIds},
        {name: "options.id", source: sources.options, parse: parseJsonIds},
        {name: "categories.id", source: sources.categories, parse: parseBracesIds},
        {name: "channels.id", source: sources.channels, parse: parseBracesIds},
    ].forEach(({name, source, parse}) => {
        const value = paramValue(name);

        if (value && source && source.length > 0) {
            hydrated.push(...findByIds(source, parse(value)));
        }
    });

    [
        {name: "status", source: sources.statuses},
        {name: "validated", source: sources.validated},
        {name: "exists[eshopId]", source: sources.eshopIds},
        {name: "inStock", source: sources.inStock},
        {name: "limitedDistribution", source: sources.limitedDistribution},
        {name: "benchmark", source: sources.benchmarks},
        {name: "demand", source: sources.demands},
    ].forEach(({name, source}) => {
        const value = paramValue(name);

        if (value !== null && source) {
            hydrated.push(...findByValue(source, value));
        }
    });

    const lockValue = paramValue("lock");
    if (lockValue && sources.locks) {
        hydrated.push(...findByIds(sources.locks, lockValue.split(",")));
    }

    // Free numeric inputs of the product list's Price and Sales tabs: the chip
    // carries the typed value, so it is rebuilt from the param value itself
    // (no source list).
    if (sources.valueFilters) {
        VALUE_FILTER_FIELDS.forEach(({type, id, param, unit}) => {
            const value = paramValue(param);

            if (null !== value && "" !== value && !isNaN(Number(value))) {
                hydrated.push({"@type": type, "id": id, "name": formatValueFilterName(value, unit), "value": Number(value)});
            }
        });
    }

    if (sources.stocks) {
        const stockLte = paramValue("stock[lte]");
        const stockGt = paramValue("stock[gt]");
        let stockValue = null;

        if (stockLte === "0") {
            stockValue = "out";
        } else if (stockGt === "0" && stockLte === "5") {
            stockValue = "middle";
        } else if (stockGt === "0") {
            stockValue = "full";
        }

        if (stockValue) {
            hydrated.push(...findByValue(sources.stocks, stockValue));
        }
    }

    const priceValue = paramValue("outletPrice[between]");
    if (priceValue && sources.withPrice) {
        const [minPrice, maxPrice] = priceValue.split("..");

        hydrated.push({"@type": "Price", "minPrice": Number(minPrice), "maxPrice": Number(maxPrice)});
    }

    return hydrated;
}
